import algoliasearch from "algoliasearch/lite"
import { default as React, useState, useMemo, useRef, useEffect } from "react"
import { ClearRefinements, Configure, CurrentRefinements, DynamicWidgets, HierarchicalMenu, HitsPerPage, InstantSearch, Stats, connectRefinementList } from "react-instantsearch-dom"
import SearchBox from "./search-box"
import SearchResult from "./knowledge-hub-search-result"
import "../../styles/knowledge-hub-search.css"
import { Disclosure } from "@headlessui/react"
import { HiCheck, HiChevronDown, HiSearch } from "react-icons/hi"
import { DEBOUNCE_TIME, createURL, searchStateToUrl, urlToSearchState } from "./utils"
import { navigate } from "gatsby"
import { kebabCase } from "lodash"
import CopyLinkButton from "../copy-link-button"
export default function KnowledgeHubSearch({ indices, heading, location }) {
  const searchClient = useMemo(
    () =>
      algoliasearch(
        process.env.GATSBY_ALGOLIA_APP_ID,
        process.env.GATSBY_ALGOLIA_SEARCH_KEY
      ),
    []
  )

  const [searchState, setSearchState] = useState(urlToSearchState(location));
  const debouncedSetStateRef = useRef(null);

  function onSearchStateChange(updatedSearchState) {
    clearTimeout(debouncedSetStateRef.current);

    debouncedSetStateRef.current = setTimeout(() => {
      navigate(searchStateToUrl(updatedSearchState));
    }, DEBOUNCE_TIME);
    setSearchState(updatedSearchState);
  }

  useEffect(() => {
    setSearchState(urlToSearchState(location));
  }, [location]);

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={indices[0].name}
      searchState={searchState}
      onSearchStateChange={onSearchStateChange}
      createURL={createURL}
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-6 xl:gap-8 relative search-filter">
        <div className="sm:col-span-2 lg:col-span-3">
          {heading ? <div className="group-copy-link group grid grid-cols-1 justify-start">
            <h2
              className="anchor-heading "
              id={kebabCase(heading).replace(/[0-9]/g, "")}
            >
              {heading}
            </h2>
            <CopyLinkButton heading={heading} />
          </div> : null}
          {indices[0].name === "Events" ? null : <Stats />}
        </div>

        <HitsPerPageSelector />

        <div className="bg-gray-300 p-4 rounded relative">
          <div className="sticky top-16">
            <div className="bg-white p-2 rounded mb-3">
              <SearchBox placeholder={"Search"} />
            </div>
            {indices[0].name !== "Internal Resources" ? <CurrentRefinements /> : null}
            <ClearRefinements clearsQuery={true} translations={{
              reset: 'Clear search and filters',
            }} className="mb-3 cursor-pointer text-gray-700 hover:text-black" />
            {indices[0].name === "Internal Resources" ?
              <div className="mb-4">
                <h2 className="font-medium text-base font-sans">Content Type</h2>
                <HierarchicalMenu
                  attributes={[
                    'irContentTypes.lvl0',
                    'irContentTypes.lvl1',
                    'irContentTypes.lvl2',
                  ]}
                />
              </div> : null}

            <DynamicWidgets
              fallbackComponent={DefaultRefinementList}
              facets={[]} />

          </div>
        </div>
        <div className="sm:col-span-2 lg:col-span-3">
          <SearchResult indices={indices} />
          <div className="mt-4"><HitsPerPageSelector /></div>
        </div>
      </div>
    </InstantSearch>
  )
}


const RefinementListDisclosure = ({
  attribute,
  items,
  refine,
  createURL,
  searchForItems,
}) => (
  <>
    {items?.length ? (
      <div className={`bg-white rounded mb-3 search-filter-${attribute}`}>
        <Disclosure>
          {({ open }) => (
            <>
              <Disclosure.Button className="py-2 px-2.5 w-full flex items-center justify-between group bg-white rounded hover:bg-gray-200/60 text-left leading-tight">
                {attribute ? (
                  <span className="font-medium text-black">{attribute === "categories.nodes.name" ? "Categories" : attribute}</span>
                ) : null}
                <HiChevronDown
                  className={`text-gray-700 w-5 ml-1 group-hover:text-black hover-grow ${open ? 'rotate-180 transform' : ''
                    }`}
                />
              </Disclosure.Button>
              <Disclosure.Panel className={'max-h-[20rem] overflow-auto'}>
                <>
                  {attribute === "categories.nodes.name" ?
                    <div
                      className="relative flex flex-row items-center mx-3 my-2 bg-gray-100 rounded px-2 py-1"
                    >
                      <HiSearch className="text-2xl mr-1 text-gray-600" aria-hidden={true} />
                      <input
                        className="w-full bg-transparent h-auto mt-0.5 cursor-text border-none focus:outline-none focus:ring-0"
                        type="search"
                        placeholder="Search Categories"
                        onChange={event => searchForItems(event.currentTarget.value)}
                      />
                    </div>

                    : null}

                  <ul className="mb-3 mt-1 px-3">
                    {items?.map((item) => (
                      <li key={item.label}>
                        <a
                          href={createURL(item.value)}
                          onClick={(event) => {
                            event.preventDefault()
                            refine(item.value)
                          }}
                          className="inline-flex items-start mb-2 md:mb-1 leading-tight text-body hover:no-underline focus:no-underline"
                        >
                          <div className={`w-3 h-3 flex-shrink-0 rounded-sm inline-flex items-center justify-center mt-1 mr-1.5 border border-gray-900 ${item.isRefined ? 'bg-blue text-white' : 'bg-white'}`}>
                            {item.isRefined ? (
                              <HiCheck strokeWidth={4} className="w-3.5 h-3.5" />
                            ) : null}
                          </div>
                          <div>
                            <span
                              className={`${item.isRefined ? 'font-medium' : ''
                                }`}
                            >
                              {item.label}
                            </span>{' '}
                            <span className="opacity-50">{item.count}</span>
                          </div>
                        </a>
                      </li>
                    ))}
                  </ul>
                </>
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      </div>
    ) : null}
  </>
)

function DefaultRefinementList({ attribute }) {
  return (
    <CustomRefinementList
      attribute={attribute}
      limit={50}
    />
  )
}

const CustomRefinementList = connectRefinementList(RefinementListDisclosure)


function HitsPerPageSelector() {
  return (
    <div className="flex justify-end items-end">
      <HitsPerPage
        className="bg-white rounded pr-2 flex justify-between"
        defaultRefinement={12}
        items={[
          { value: 12, label: "12 results per page" },
          { value: 24, label: "24 results per page" },
          { value: 48, label: "48 results per page" },
        ]}
      />
    </div>
  )
}